import { useCallback } from 'react';
import { showAlert } from '@silverfort/sf-components';
import { LocalAccount } from '../../../types/LocalAccount';
import { usePopup } from '../../../context/PopupProvider';
import { UNBIND_POPUP_CONFIG } from '../popupConfigs';
import { useUnbindLocalAccount, useUnbindLocalAccounts } from '../../../api';

interface UseUnbindLocalAccountsPopupParams {
    accounts: LocalAccount[];
    onAccountsUnbound?: () => void;
}

interface UseUnbindLocalAccountPopupParams {
    account: LocalAccount;
    onAccountUnbound?: () => void;
}

interface UseUnbindLocalAccountsPopupReturnType {
    openUnbindLocalAccountsPopup: () => void;
}

export const useUnbindLocalAccountPopup = ({
    account,
    onAccountUnbound
}: UseUnbindLocalAccountPopupParams): UseUnbindLocalAccountsPopupReturnType => {
    const { openPopup, closePopup } = usePopup();

    const handleSuccess = useCallback(() => {
        closePopup();
        showAlert(`${account.userName} was successfully unbound`, 'success', { title: 'Unbound successfully' });
        onAccountUnbound?.();
    }, [closePopup, onAccountUnbound]);

    const handleError = useCallback(() => {
        closePopup();
        showAlert(`Failed to unbind ${account.userName}`, 'error', { title: 'Unbind failed' });
    }, [closePopup]);

    const { unbindLocalAccount } = useUnbindLocalAccount({
        onSuccess: handleSuccess,
        onError: handleError
    });

    const openUnbindLocalAccountsPopup = useCallback(() => {
        const config = {
            ...UNBIND_POPUP_CONFIG,
            onApprove: async () => {
                await unbindLocalAccount(account.guid);
            }
        };

        openPopup(config);
    }, [account, unbindLocalAccount, openPopup]);

    return { openUnbindLocalAccountsPopup };
};

export const useUnbindLocalAccountsPopup = ({
    accounts,
    onAccountsUnbound
}: UseUnbindLocalAccountsPopupParams): UseUnbindLocalAccountsPopupReturnType => {
    const { openPopup, closePopup } = usePopup();

    const handleSuccess = useCallback(() => {
        closePopup();
        showAlert('Local accounts were successfully unbound', 'success', { title: 'Unbound successfully' });
        onAccountsUnbound?.();
    }, [closePopup, onAccountsUnbound]);

    const handleError = useCallback(() => {
        closePopup();
        showAlert('Failed to unbind local accounts', 'error', { title: 'Unbind failed' });
    }, [closePopup]);

    const { unbindLocalAccounts } = useUnbindLocalAccounts({
        onSuccess: handleSuccess,
        onError: handleError
    });

    const openUnbindLocalAccountsPopup = useCallback(() => {
        const config = {
            ...UNBIND_POPUP_CONFIG,
            onApprove: async () => {
                await unbindLocalAccounts(accounts.map(({ guid }) => guid));
            }
        };

        openPopup(config);
    }, [accounts, unbindLocalAccounts, openPopup]);

    return { openUnbindLocalAccountsPopup };
};
